import { NavLink, useLocation } from 'react-router';
import { Tooltip } from '@dynamon-as/ui';
import type { NavItem, RenderLinkProps } from '@dynamon-as/ui';
import { useNavigationGuard } from '@contexts';
import styles from './DepartmentNavLink.module.css';
import type { FC } from 'react';

export interface DepartmentNavItem extends NavItem {
    /** Extended help text shown in the item tooltip. */
    description: string;
}

/**
 * Single department sidebar link, rendered by the @dynamon-as/ui
 * Sidebar through its renderLink contract. Routes clicks through the
 * dirty-state navigation guard instead of navigating directly.
 */
const DepartmentNavLink: FC<RenderLinkProps> = ({
    item,
    isActive,
    className,
    iconClassName,
    labelClassName,
}) => {
    const { pathname } = useLocation();
    const { guardedNavigate } = useNavigationGuard();

    // The library types nav items as `NavItem`; the items built by
    // the SidebarLayout carry the extra `description` help text.
    const { description } = item as DepartmentNavItem;
    const descriptionText = `${item.label}: ${description}`;

    return (
        <Tooltip
            text={descriptionText}
            placement="end"
            className={styles.tooltipTrigger}
        >
            <NavLink
                to={item.href}
                className={className}
                aria-label={descriptionText}
                aria-current={isActive ? 'page' : undefined}
                onClick={(event) => {
                    event.preventDefault();
                    if (pathname !== item.href) {
                        guardedNavigate(item.href);
                    }
                }}
            >
                <span className={iconClassName}>{item.icon}</span>
                <span className={labelClassName}>{item.label}</span>
            </NavLink>
        </Tooltip>
    );
};

export default DepartmentNavLink;
